import { configureStore, combineReducers, ThunkAction, Action } from "@reduxjs/toolkit"
import mealReducer from "../state/meals/mealSlice"
import userReducer from "../state/user/userSlice"
import clientsReducer from "../state/clients/clientsSlice"
import { OpenAPI } from "../core/OpenAPI"
import { createInitialUserState, decodeJwtToUser } from "../state/user/userSlice"
import { clearPersistedToken, getPersistedToken } from "../common/authStorage"

const appReducer = combineReducers({
  meal: mealReducer,
  user: userReducer,
  client: clientsReducer,
})

export type RootState = ReturnType<typeof appReducer>
type PersistedRootState = Pick<RootState, "user">
type PreloadedRootState = Partial<RootState>

const ONBOARDING_DRAFT_STORAGE_KEY = "dietWeb.onboardingDraft"
const PREFERENCES_STORAGE_KEY = "dietWeb.preferences"
OpenAPI.TOKEN = async () => getPersistedToken() || ''

function sanitizeDraftUser(user: RootState["user"]["newUser"]) {
  return {
    ...user,
    id: undefined,
    password: undefined,
    roles: undefined,
    bodyMeasurements: user.bodyMeasurements?.map((measurement) => ({ ...measurement })),
    allergies: user.allergies ? [...user.allergies] : undefined,
    languageCodes: user.languageCodes ? [...user.languageCodes] : undefined,
  }
}

function hasPersistableDraft(user: RootState["user"]["newUser"], timezone: string) {
  const initialDraft = sanitizeDraftUser(createInitialUserState(timezone).newUser)
  const currentDraft = sanitizeDraftUser(user)

  return JSON.stringify(currentDraft) !== JSON.stringify(initialDraft)
}

/**
 * Load persisted onboarding draft and lightweight user preferences.
 */
function loadState(): Partial<PersistedRootState> | undefined {
  try {
    const serializedPreferences = localStorage.getItem(PREFERENCES_STORAGE_KEY)
    const serializedDraft = localStorage.getItem(ONBOARDING_DRAFT_STORAGE_KEY)

    const preferences = serializedPreferences
      ? JSON.parse(serializedPreferences) as Pick<RootState["user"], "language" | "timezone">
      : undefined
    const draft = serializedDraft
      ? JSON.parse(serializedDraft) as RootState["user"]["newUser"]
      : undefined
    const jwt = getPersistedToken()

    if (!preferences && !draft && !jwt) return undefined

    const timezone = preferences?.timezone ?? draft?.timezone
    const initialUserState = createInitialUserState(timezone)
    const decodedUser = decodeJwtToUser(jwt)

    if (jwt && !decodedUser.id) {
      clearPersistedToken()
    }

    return {
      user: {
        ...initialUserState,
        jwt: decodedUser.id ? jwt : undefined,
        loginStatus: decodedUser.id ? 'OK' : initialUserState.loginStatus,
        language: preferences?.language ?? initialUserState.language,
        timezone: preferences?.timezone ?? initialUserState.timezone,
        user: {
          ...initialUserState.user,
          ...decodedUser,
          timezone: preferences?.timezone ?? initialUserState.user.timezone,
        },
        newUser: draft
          ? {
              ...initialUserState.newUser,
              ...draft,
              password: undefined,
            }
          : initialUserState.newUser,
      },
    }
  } catch (_err) {
    return undefined
  }
}

/**
 * Save only onboarding draft and lightweight preferences.
 */
function saveState(state: RootState) {
  try {
    const serializedPreferences = JSON.stringify({
      language: state.user.language,
      timezone: state.user.timezone,
    })

    localStorage.setItem(PREFERENCES_STORAGE_KEY, serializedPreferences)

    if (hasPersistableDraft(state.user.newUser, state.user.timezone)) {
      const serializedDraft = JSON.stringify(sanitizeDraftUser(state.user.newUser))
      localStorage.setItem(ONBOARDING_DRAFT_STORAGE_KEY, serializedDraft)
    } else {
      localStorage.removeItem(ONBOARDING_DRAFT_STORAGE_KEY)
    }
  } catch (_err) {}
}

/**
 * Root reducer with logout reset
 */
const rootReducer = (state: RootState | PreloadedRootState | undefined, action: any) => {
  if (action.type === "user/logout") {
    OpenAPI.TOKEN = undefined
    localStorage.removeItem(ONBOARDING_DRAFT_STORAGE_KEY)
    clearPersistedToken()

    const resetState = appReducer(undefined, { type: "" })
    const preservedLanguage = state?.user?.language ?? resetState.user.language
    const preservedTimezone = state?.user?.timezone ?? resetState.user.timezone

    return {
      ...resetState,
      user: {
        ...resetState.user,
        language: preservedLanguage,
        timezone: preservedTimezone,
        user: {
          ...resetState.user.user,
          timezone: preservedTimezone,
        },
        newUser: {
          ...resetState.user.newUser,
          timezone: preservedTimezone,
        },
      },
    }
  }

  if (action.type === "user/clearOnboardingDraft") {
    localStorage.removeItem(ONBOARDING_DRAFT_STORAGE_KEY)
  }

  return appReducer(state as RootState | undefined, action)
}

const persistedState = loadState()

/**
 * Store
 */
export const store = configureStore({
  reducer: rootReducer,
  preloadedState: persistedState as PreloadedRootState | undefined,
})

store.subscribe(() => {
  saveState(store.getState())
})

export type AppDispatch = typeof store.dispatch

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>
